import React, { useState, useEffect } from 'react';
import './LoadingOverlay.css';

const DOC_CONFIG = {
  PAN: {
    icon: '🆔', color: '#6366f1', label: 'PAN Card',
    steps: [
      '🔍 Initializing document scanner...',
      '📸 Capturing PAN Card image...',
      '🎯 Detecting document boundaries...',
      '📝 Extracting PAN number...',
      '👤 Reading name & father\'s name...',
      '📅 Parsing date of birth...',
      '✨ Applying AI enhancement...',
      '🔎 Validating PAN format (ABCDE1234F)...',
      '🧠 Running OCR engine...',
      '📊 Calculating confidence score...',
      '✅ Verifying extracted data...',
      '🎯 Finalizing results...',
    ],
  },
  AADHAAR: {
    icon: '🪪', color: '#8b5cf6', label: 'Aadhaar Card',
    steps: [
      '🔍 Initializing dual-side scanner...',
      '📸 Processing front image...',
      '📸 Processing back image...',
      '🎯 Detecting document edges...',
      '🆔 Extracting 12-digit Aadhaar number...',
      '👤 Reading personal details...',
      '📅 Parsing date of birth...',
      '⚧️ Identifying gender information...',
      '📍 Extracting full address...',
      '🏙️ Reading city, state & pincode...',
      '🔎 Cross-validating both sides...',
      '✅ Verifying Aadhaar format...',
      '📊 Computing accuracy metrics...',
    ],
  },
  DRIVING: {
    icon: '🚗', color: '#06b6d4', label: 'Driving License',
    steps: [
      '🔍 Scanning driving license...',
      '🎯 Detecting license boundaries...',
      '🆔 Extracting DL number...',
      '👤 Reading holder name...',
      '📅 Extracting date of birth...',
      '📅 Reading issue & validity dates...',
      '🩸 Identifying blood group...',
      '🚗 Detecting vehicle classes...',
      '📍 Extracting address details...',
      '🔎 Validating license format...',
      '✅ Cross-checking dates...',
      '📊 Generating confidence score...',
    ],
  },
  VOTER: {
    icon: '🗳️', color: '#10b981', label: 'Voter ID',
    steps: [
      '🔍 Processing Voter ID card...',
      '🎯 Detecting card boundaries...',
      '🆔 Extracting EPIC number...',
      '👤 Reading voter name...',
      '👨 Parsing relative\'s name...',
      '📅 Extracting date of birth...',
      '⚧️ Identifying gender...',
      '📍 Reading full address...',
      '🗳️ Extracting polling station...',
      '🔎 Validating EPIC format...',
      '✅ Verifying extracted data...',
      '📊 Computing accuracy...',
    ],
  },
  RESUME: {
    icon: '📄', color: '#f59e0b', label: 'Resume / CV',
    steps: [
      '📄 Loading resume document...',
      '🔍 Scanning all pages...',
      '📝 Extracting contact information...',
      '📧 Reading email & phone...',
      '💼 Analyzing work experience...',
      '🏢 Extracting company names & dates...',
      '🎓 Reading education details...',
      '💻 Scanning technical skills...',
      '🔧 Identifying tools & technologies...',
      '🎯 Calculating ATS score...',
      '📊 Analyzing keyword density...',
      '📈 Computing match percentage...',
      '✅ Finalizing resume analysis...',
    ],
  },
  NOTICEAI: {
    icon: '⚖️', color: '#ef4444', label: 'Tax Notice / Legal',
    steps: [
      '⚖️ Loading legal document...',
      '📄 Scanning notice pages...',
      '🏢 Extracting entity name & address...',
      '🆔 Parsing registration number...',
      '📅 Extracting document date...',
      '⚖️ Identifying forum/authority...',
      '📜 Extracting relevant sections...',
      '💰 Detecting tax amounts...',
      '⚠️ Identifying issues raised...',
      '📋 Extracting information requests...',
      '💸 Analyzing interest & penalties...',
      '📊 Generating executive summary...',
      '✅ Finalizing notice analysis...',
    ],
  },
  SUMMARY: {
    icon: '🤖', color: '#a855f7', label: 'AI Summary',
    steps: [
      '📄 Loading document...',
      '🔍 Scanning all pages...',
      '📝 Extracting text content...',
      '🧠 Analyzing document structure...',
      '🎯 Identifying key topics...',
      '💡 Extracting main points...',
      '📊 Analyzing content density...',
      '📈 Ranking important sentences...',
      '✨ Applying AI summarization...',
      '🤖 Running NLP algorithms...',
      '📋 Generating key insights...',
      '📊 Calculating compression ratio...',
      '✅ Finalizing intelligent summary...',
    ],
  },
  BANK_STATEMENT: {
    icon: '🏦', color: '#34d399', label: 'Bank Statement',
    steps: [
      '🏦 Initializing bank statement processor...',
      '📄 Loading PDF document...',
      '🔍 Detecting bank format & layout...',
      '🏛️ Identifying financial institution...',
      '📋 Extracting account information...',
      '👤 Reading account holder details...',
      '📅 Parsing statement period...',
      '💰 Calculating opening & closing balance...',
      '📊 Extracting transaction records...',
      '💳 Analyzing credit transactions...',
      '💸 Analyzing debit transactions...',
      '🏷️ Categorizing transactions...',
      '💼 Identifying salary deposits...',
      '🏠 Detecting EMI payments...',
      '🛒 Analyzing spending patterns...',
      '📈 Computing cash flow metrics...',
      '🎯 Computing credit eligibility score...',
      '🔍 Running fraud detection algorithms...',
      '⚖️ Performing AML compliance check...',
      '📊 Analyzing merchant spending...',
      '✅ Generating comprehensive credit report...',
    ],
  },
  DEFAULT: {
    icon: '📁', color: '#6366f1', label: 'Document',
    steps: [
      '🔍 Initializing document processor...',
      '📸 Scanning document...',
      '🎯 Detecting content areas...',
      '📝 Extracting text information...',
      '✨ Applying AI analysis...',
      '🧠 Running OCR engine...',
      '🔎 Validating extracted data...',
      '📊 Computing confidence metrics...',
      '✅ Finalizing results...',
    ],
  },
};

const LoadingOverlay = ({ documentType, pdfCategory }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');

  const key = documentType === 'PDF' ? (pdfCategory || 'DEFAULT') : (documentType || 'DEFAULT');
  const config = DOC_CONFIG[key] || DOC_CONFIG.DEFAULT;
  const steps = config.steps;

  useEffect(() => {
    setStepIndex(0);
    setProgress(0);
  }, [documentType, pdfCategory]);

  useEffect(() => {
    const msgInterval = setInterval(() => {
      setStepIndex(prev => (prev + 1) % steps.length);
    }, 2200);
    return () => clearInterval(msgInterval);
  }, [steps.length]);

  useEffect(() => {
    const progInterval = setInterval(() => {
      setProgress(prev => prev >= 95 ? 95 : prev + Math.random() * 3);
    }, 400);
    return () => clearInterval(progInterval);
  }, []);

  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(dotsInterval);
  }, []);

  const completedSteps = Math.floor((stepIndex / steps.length) * 5);

  return (
    <div className="lv2-overlay">
      <div className="lv2-backdrop" />

      {/* Floating particles */}
      <div className="lv2-particles">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="lv2-particle" style={{
            '--delay': `${i * 0.4}s`,
            '--x': `${10 + i * 7}%`,
            '--color': config.color,
          }} />
        ))}
      </div>

      <div className="lv2-modal" style={{ '--accent': config.color }}>
        {/* Top gradient bar */}
        <div className="lv2-top-bar" style={{ background: `linear-gradient(90deg, ${config.color}, #8b5cf6, ${config.color})` }} />

        {/* Header */}
        <div className="lv2-header">
          <div className="lv2-icon-wrap" style={{ '--accent': config.color }}>
            <div className="lv2-icon-ring" />
            <div className="lv2-icon-ring lv2-ring2" />
            <div className="lv2-icon-ring lv2-ring3" />
            <span className="lv2-doc-icon">{config.icon}</span>
          </div>
          <div>
            <h2 className="lv2-title">Analyzing Document{dots}</h2>
            <span className="lv2-subtitle" style={{ color: config.color }}>{config.label}</span>
          </div>
        </div>

        {/* Current step message */}
        <div className="lv2-message-box" style={{ borderColor: `${config.color}33` }}>
          <span className="lv2-message" key={stepIndex}>{steps[stepIndex]}</span>
        </div>

        {/* Progress bar */}
        <div className="lv2-progress-wrap">
          <div className="lv2-progress-track">
            <div className="lv2-progress-fill" style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${config.color}, #8b5cf6)`,
            }} />
            <div className="lv2-progress-glow" style={{
              left: `${progress}%`,
              background: config.color,
            }} />
          </div>
          <span className="lv2-progress-pct" style={{ color: config.color }}>{Math.round(progress)}%</span>
        </div>

        {/* Step dots */}
        <div className="lv2-step-dots">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`lv2-dot ${i <= completedSteps ? 'done' : ''}`}
              style={i <= completedSteps ? { background: config.color, boxShadow: `0 0 8px ${config.color}` } : {}} />
          ))}
        </div>

        {/* Footer hint */}
        <p className="lv2-hint">AI-powered OCR • Multi-engine processing • Enterprise grade</p>
      </div>
    </div>
  );
};

export default LoadingOverlay;
